/**
 * ProjectStateManager - Central service for managing project state persistence
 *
 * This singleton service handles loading and saving of all project state files
 * from the .editor-state directory. It coordinates between the file system and
 * the various Zustand stores.
 *
 * Uses FileSystemService for all file operations via the File System Access API.
 */

import {
  ProjectEditorState,
  SceneEditorState,
  ScriptEditorProjectState,
  AnimationProjectState,
  SlotEditorProjectState,
  ProjectStateBundle,
  PROJECT_STATE_FILES,
  EDITOR_STATE_DIR,
  DEFAULT_EDITOR_STATE,
  DEFAULT_SCENE_STATE,
  DEFAULT_SCRIPT_EDITOR_STATE,
  DEFAULT_ANIMATION_STATE,
  DEFAULT_SLOT_STATE,
  SlotAssetGeneratorProjectState,
  DEFAULT_SLOT_ASSET_GENERATOR_STATE,
  SlotScreenGeneratorProjectState,
  DEFAULT_SLOT_SCREEN_GENERATOR_STATE,
} from '../../core/types/project-state.types';
import {
  readFile,
  writeFile,
  createDirectory,
  fileExists,
  hasProjectRoot,
} from './FileSystemService';

/**
 * Deep merge utility that combines source into target
 * Arrays are replaced, not merged
 */
function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target } as T;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key as keyof T];
      const targetValue = target[key as keyof T];

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        targetValue !== undefined &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (result as any)[key] = deepMerge(
          targetValue as object,
          sourceValue as object
        );
      } else if (sourceValue !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (result as any)[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Singleton class for managing project state persistence
 */
class ProjectStateManagerClass {
  private projectPath: string | null = null;
  private isInitialized = false;

  initialize(projectPath: string): void {
    this.projectPath = projectPath;
    this.isInitialized = true;
  }

  getProjectPath(): string {
    if (!this.isInitialized || !this.projectPath) {
      throw new Error('ProjectStateManager not initialized. Call initialize() first.');
    }
    return this.projectPath;
  }

  isReady(): boolean {
    return this.isInitialized && this.projectPath !== null && hasProjectRoot();
  }

  private async ensureStateDirectory(): Promise<void> {
    try {
      await createDirectory(EDITOR_STATE_DIR, true);
    } catch (error) {
      console.debug('State directory creation:', error);
    }
  }

  private async readJsonFile<T>(relativePath: string): Promise<T | null> {
    try {
      const exists = await fileExists(relativePath);
      if (!exists) return null;

      const content = await readFile(relativePath);
      return JSON.parse(content) as T;
    } catch (error) {
      // Only log unexpected errors (parse failures, permission issues), not missing files
      console.debug(`Could not read ${relativePath}:`, error);
      return null;
    }
  }

  private async writeJsonFile<T>(relativePath: string, data: T): Promise<void> {
    const content = JSON.stringify(data, null, 2);
    await writeFile(relativePath, content);
  }

  mergeWithDefaults<T extends object>(
    loaded: Partial<T> | null,
    defaults: T
  ): T {
    if (!loaded) {
      return { ...defaults, lastModified: Date.now() } as T;
    }
    return deepMerge(defaults, loaded);
  }

  // ===========================================================================
  // Individual State Loaders
  // ===========================================================================

  async loadEditorState(): Promise<ProjectEditorState> {
    const loaded = await this.readJsonFile<Partial<ProjectEditorState>>(PROJECT_STATE_FILES.EDITOR_STATE);
    return this.mergeWithDefaults(loaded, DEFAULT_EDITOR_STATE);
  }

  async loadSceneState(): Promise<SceneEditorState> {
    const loaded = await this.readJsonFile<Partial<SceneEditorState>>(PROJECT_STATE_FILES.SCENE_STATE);
    return this.mergeWithDefaults(loaded, DEFAULT_SCENE_STATE);
  }

  async loadScriptEditorState(): Promise<ScriptEditorProjectState> {
    const loaded = await this.readJsonFile<Partial<ScriptEditorProjectState>>(PROJECT_STATE_FILES.SCRIPT_TABS);
    return this.mergeWithDefaults(loaded, DEFAULT_SCRIPT_EDITOR_STATE);
  }

  async loadAnimationState(): Promise<AnimationProjectState> {
    const loaded = await this.readJsonFile<Partial<AnimationProjectState>>(PROJECT_STATE_FILES.ANIMATION_STATE);
    return this.mergeWithDefaults(loaded, DEFAULT_ANIMATION_STATE);
  }

  async loadSlotEditorState(): Promise<SlotEditorProjectState> {
    const loaded = await this.readJsonFile<Partial<SlotEditorProjectState>>(PROJECT_STATE_FILES.SLOT_STATE);
    return this.mergeWithDefaults(loaded, DEFAULT_SLOT_STATE);
  }

  async loadSlotAssetGeneratorState(): Promise<SlotAssetGeneratorProjectState> {
    const loaded = await this.readJsonFile<Partial<SlotAssetGeneratorProjectState>>(
      PROJECT_STATE_FILES.SLOT_ASSET_GENERATOR
    );
    return this.mergeWithDefaults(loaded, DEFAULT_SLOT_ASSET_GENERATOR_STATE);
  }

  async loadSlotScreenGeneratorState(): Promise<SlotScreenGeneratorProjectState> {
    const loaded = await this.readJsonFile<Partial<SlotScreenGeneratorProjectState>>(
      PROJECT_STATE_FILES.SLOT_SCREEN_GENERATOR
    );
    return this.mergeWithDefaults(loaded, DEFAULT_SLOT_SCREEN_GENERATOR_STATE);
  }

  // ===========================================================================
  // Individual State Savers
  // ===========================================================================

  async saveEditorState(state: ProjectEditorState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.EDITOR_STATE, stateToSave);
  }

  async saveSceneState(state: SceneEditorState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.SCENE_STATE, stateToSave);
  }

  async saveScriptEditorState(state: ScriptEditorProjectState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.SCRIPT_TABS, stateToSave);
  }

  async saveAnimationState(state: AnimationProjectState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.ANIMATION_STATE, stateToSave);
  }

  async saveSlotEditorState(state: SlotEditorProjectState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, version: 1, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.SLOT_STATE, stateToSave);
  }

  async saveSlotAssetGeneratorState(state: SlotAssetGeneratorProjectState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, version: 1, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.SLOT_ASSET_GENERATOR, stateToSave);
  }

  async saveSlotScreenGeneratorState(state: SlotScreenGeneratorProjectState): Promise<void> {
    await this.ensureStateDirectory();
    const stateToSave = { ...state, version: 1, lastModified: Date.now() };
    await this.writeJsonFile(PROJECT_STATE_FILES.SLOT_SCREEN_GENERATOR, stateToSave);
  }

  // ===========================================================================
  // Bulk Operations
  // ===========================================================================

  async loadProjectState(): Promise<ProjectStateBundle> {
    const [
      editorState,
      sceneState,
      scriptEditorState,
      animationState,
      slotEditorState,
      slotAssetGeneratorState,
      slotScreenGeneratorState,
    ] = await Promise.all([
      this.loadEditorState(),
      this.loadSceneState(),
      this.loadScriptEditorState(),
      this.loadAnimationState(),
      this.loadSlotEditorState(),
      this.loadSlotAssetGeneratorState(),
      this.loadSlotScreenGeneratorState(),
    ]);

    return {
      editorState,
      sceneState,
      scriptEditorState,
      animationState,
      slotEditorState,
      slotAssetGeneratorState,
      slotScreenGeneratorState,
    };
  }

  async saveProjectState(bundle: ProjectStateBundle): Promise<void> {
    await this.ensureStateDirectory();

    const saves: Promise<void>[] = [
      this.saveEditorState(bundle.editorState),
      this.saveSceneState(bundle.sceneState),
      this.saveScriptEditorState(bundle.scriptEditorState),
      this.saveAnimationState(bundle.animationState),
      this.saveSlotEditorState(bundle.slotEditorState),
    ];

    if (bundle.slotAssetGeneratorState) {
      saves.push(this.saveSlotAssetGeneratorState(bundle.slotAssetGeneratorState));
    }

    if (bundle.slotScreenGeneratorState) {
      saves.push(this.saveSlotScreenGeneratorState(bundle.slotScreenGeneratorState));
    }

    await Promise.all(saves);
  }

  async initializeStores(bundle: ProjectStateBundle): Promise<void> {
    const { useEditorStore } = await import('../store/editorStore');
    const { useSceneStore } = await import('../store/sceneStore');
    const { useScriptEditorStore } = await import('../store/scriptEditorStore');
    const { useAnimationStore } = await import('../store/animationStore');

    const editorStore = useEditorStore.getState();
    const sceneStore = useSceneStore.getState();
    const scriptEditorStore = useScriptEditorStore.getState();
    const animationStore = useAnimationStore.getState();

    if ('loadProjectState' in editorStore && typeof editorStore.loadProjectState === 'function') {
      editorStore.loadProjectState(bundle.editorState);
    }

    if ('loadProjectState' in sceneStore && typeof sceneStore.loadProjectState === 'function') {
      sceneStore.loadProjectState(bundle.sceneState);
    }

    if ('loadProjectState' in scriptEditorStore && typeof scriptEditorStore.loadProjectState === 'function') {
      scriptEditorStore.loadProjectState(bundle.scriptEditorState);
    }

    if ('loadProjectState' in animationStore && typeof animationStore.loadProjectState === 'function') {
      animationStore.loadProjectState(bundle.animationState);
    }
  }

  async gatherStateFromStores(): Promise<ProjectStateBundle> {
    const { useEditorStore } = await import('../store/editorStore');
    const { useSceneStore } = await import('../store/sceneStore');
    const { useScriptEditorStore } = await import('../store/scriptEditorStore');
    const { useAnimationStore } = await import('../store/animationStore');

    const editorStore = useEditorStore.getState();
    const sceneStore = useSceneStore.getState();
    const scriptEditorStore = useScriptEditorStore.getState();
    const animationStore = useAnimationStore.getState();

    const editorState: ProjectEditorState =
      'getProjectState' in editorStore && typeof editorStore.getProjectState === 'function'
        ? editorStore.getProjectState()
        : {
            ...DEFAULT_EDITOR_STATE,
            projectName: editorStore.projectName || DEFAULT_EDITOR_STATE.projectName,
            playMode: editorStore.playMode || DEFAULT_EDITOR_STATE.playMode,
          };

    const sceneState: SceneEditorState =
      'getProjectState' in sceneStore && typeof sceneStore.getProjectState === 'function'
        ? sceneStore.getProjectState()
        : {
            ...DEFAULT_SCENE_STATE,
            sceneId: sceneStore.sceneId || DEFAULT_SCENE_STATE.sceneId,
            sceneName: sceneStore.sceneName || DEFAULT_SCENE_STATE.sceneName,
            gameObjects: sceneStore.gameObjects || DEFAULT_SCENE_STATE.gameObjects,
            rootIds: sceneStore.rootIds || DEFAULT_SCENE_STATE.rootIds,
            selectedIds: sceneStore.selectedIds || DEFAULT_SCENE_STATE.selectedIds,
          };

    const scriptEditorState: ScriptEditorProjectState =
      'getProjectState' in scriptEditorStore && typeof scriptEditorStore.getProjectState === 'function'
        ? scriptEditorStore.getProjectState()
        : {
            ...DEFAULT_SCRIPT_EDITOR_STATE,
            openTabs: scriptEditorStore.openTabs || DEFAULT_SCRIPT_EDITOR_STATE.openTabs,
            activeTabId: scriptEditorStore.activeTabId || DEFAULT_SCRIPT_EDITOR_STATE.activeTabId,
            sidebarWidth: scriptEditorStore.sidebarWidth || DEFAULT_SCRIPT_EDITOR_STATE.sidebarWidth,
            sidebarVisible: scriptEditorStore.sidebarVisible ?? DEFAULT_SCRIPT_EDITOR_STATE.sidebarVisible,
            bottomPanelHeight: scriptEditorStore.bottomPanelHeight || DEFAULT_SCRIPT_EDITOR_STATE.bottomPanelHeight,
            bottomPanelVisible: scriptEditorStore.bottomPanelVisible ?? DEFAULT_SCRIPT_EDITOR_STATE.bottomPanelVisible,
            editorSettings: scriptEditorStore.settings || DEFAULT_SCRIPT_EDITOR_STATE.editorSettings,
          };

    const animationState: AnimationProjectState =
      'getProjectState' in animationStore && typeof animationStore.getProjectState === 'function'
        ? animationStore.getProjectState()
        : {
            ...DEFAULT_ANIMATION_STATE,
            animations: animationStore.animations || DEFAULT_ANIMATION_STATE.animations,
            selectedAnimationId: animationStore.selectedAnimationId || DEFAULT_ANIMATION_STATE.selectedAnimationId,
            editorPreferences: {
              snapToFrames: animationStore.snapToFrames ?? DEFAULT_ANIMATION_STATE.editorPreferences.snapToFrames,
              autoKeyframe: animationStore.autoKeyframe ?? DEFAULT_ANIMATION_STATE.editorPreferences.autoKeyframe,
              showGrid: animationStore.showGrid ?? DEFAULT_ANIMATION_STATE.editorPreferences.showGrid,
              gridSubdivisions: animationStore.gridSubdivisions || DEFAULT_ANIMATION_STATE.editorPreferences.gridSubdivisions,
              onionSkinEnabled: animationStore.onionSkin?.enabled ?? DEFAULT_ANIMATION_STATE.editorPreferences.onionSkinEnabled,
            },
          };

    return {
      editorState,
      sceneState,
      scriptEditorState,
      animationState,
      slotEditorState: { ...DEFAULT_SLOT_STATE, lastModified: Date.now() },
    };
  }

  clearProjectState(): void {
    this.projectPath = null;
    this.isInitialized = false;
  }

  async resetStores(): Promise<void> {
    const { useEditorStore } = await import('../store/editorStore');
    const { useSceneStore } = await import('../store/sceneStore');
    const { useScriptEditorStore } = await import('../store/scriptEditorStore');
    const { useAnimationStore } = await import('../store/animationStore');

    const stores = [
      useEditorStore.getState(),
      useSceneStore.getState(),
      useScriptEditorStore.getState(),
      useAnimationStore.getState(),
    ];

    for (const store of stores) {
      if ('resetProjectState' in store && typeof store.resetProjectState === 'function') {
        store.resetProjectState();
      }
    }
  }
}

// Export singleton instance
export const ProjectStateManager = new ProjectStateManagerClass();

// Also export the class for testing purposes
export { ProjectStateManagerClass };
